import React from 'react';
import {
  Typography,
  TextField,
  Grid,
  FormControlLabel,
  Switch,
  Divider,
} from '@mui/material';

/**
 * LayoutSection Component
 *
 * Displays the layout/component customization section in the theme customizer.
 * Includes theme properties, layout settings, and theme status.
 *
 * @param {Object} props
 * @param {Object} props.themeData - Current theme data object
 * @param {Function} props.updateThemeData - Function to update theme data
 */
function LayoutSection({ themeData, updateThemeData }) {
  return (
    <>
      <Typography variant="h6" gutterBottom>
        Theme Properties
      </Typography>
      <TextField
        label="Theme Name"
        value={themeData.theme_name || ''}
        onChange={e => updateThemeData('theme_name', e.target.value)}
        margin="normal"
        fullWidth
      />

      <TextField
        label="Theme Description"
        multiline
        rows={3}
        value={themeData.theme_description || ''}
        onChange={e => updateThemeData('theme_description', e.target.value)}
        margin="normal"
        fullWidth
      />

      <Divider sx={{ my: 3 }} />

      <Typography variant="h6" gutterBottom>
        Layout Settings
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            label="Border Radius"
            type="number"
            value={themeData.border_radius || 8}
            onChange={e =>
              updateThemeData('border_radius', parseInt(e.target.value) || 8)
            }
            helperText="Border radius in pixels"
            margin="normal"
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            label="Spacing Unit"
            type="number"
            value={themeData.spacing_unit || 8}
            onChange={e =>
              updateThemeData('spacing_unit', parseInt(e.target.value) || 8)
            }
            helperText="Base spacing unit in pixels"
            margin="normal"
            fullWidth
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      <Typography variant="h6" gutterBottom>
        Shadow & Elevation
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Control depth and elevation for cards, modals, and popovers
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 4 }}>
          <TextField
            label="Card Shadow"
            value={themeData.card_shadow || '0 2px 8px rgba(0,0,0,0.1)'}
            onChange={e => updateThemeData('card_shadow', e.target.value)}
            helperText="CSS box-shadow for cards"
            margin="normal"
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <TextField
            label="Modal Shadow"
            value={themeData.modal_shadow || '0 8px 32px rgba(0,0,0,0.2)'}
            onChange={e => updateThemeData('modal_shadow', e.target.value)}
            helperText="CSS box-shadow for modals"
            margin="normal"
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <TextField
            label="Hover Shadow"
            value={themeData.hover_shadow || '0 4px 16px rgba(0,0,0,0.15)'}
            onChange={e => updateThemeData('hover_shadow', e.target.value)}
            helperText="Shadow on hover states"
            margin="normal"
            fullWidth
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      <Typography variant="h6" gutterBottom>
        Theme Status
      </Typography>
      <FormControlLabel
        control={
          <Switch
            checked={themeData.is_active || false}
            onChange={e => updateThemeData('is_active', e.target.checked)}
            color="primary"
          />
        }
        label="Active Theme"
      />
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        sx={{ mt: 1 }}
      >
        Enable this theme for the tenant
      </Typography>
    </>
  );
}

export default LayoutSection;
